import { html, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { BlocksTabSelectEvent } from '../../events/blocks-tab-select';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import type { TabBehavior, TabSize, TabStyleVariant } from './tab.model';
import styles from './tab.styles';

/**
 * @slot default - The component content
 * @fires bl-tab-select - emitted when a tab is selected
 * @csspart base - The component's base wrapper.
 * @csspart button - The component's button wrapper.
 * @csspart wrap - The component's wrapper.
 * @csspart part-default - The component's default slot.
 */

export class BlocksTab extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Index to track tab
   */
  @property({ type: Number })
  index: number | undefined;

  /**
   * Aria labelledby property
   * 1) Used to connect tab trigger and tab panel for accessibility
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   * Set Active property
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Tab ariaControls
   * 1) Set to the id to connect the tab trigger to the tab panel
   *
   */
  @property({ type: String })
  ariaControls: string | undefined;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**contrast** formats tabs in a pill shape</li>
   * <li>**span-container** Expands the border of the tab container to full width</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: TabStyleVariant;

  /**
   * Behavior variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**responsive-full-width** allows for natural width on large screens and full width on small screens</li>
   * <li>**full-width** allows  full width on all screens</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  behavior: TabBehavior;

  /** size variants - not applied for `contrast` variant.
   * **lg** adds extra padding to the default tab
   */
  @property({ type: String })
  size: TabSize;

  constructor() {
    super();
    this.addEventListener('click', this.handleTabSelected);
    this.addEventListener('keydown', this.handleKeydown);
  }

  private handleTabSelected(): void {
    if (this.disabled) {
      return;
    }
    this.dispatch({ eventName: 'bl-tab-select', detailObj: {} }) as BlocksTabSelectEvent;
  }

  protected firstUpdated(): void {
    this.setAttribute('role', 'tab');
    this.setAttribute('tabindex', '-1');
    this.setAttribute('aria-selected', 'false');
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('ariaLabelledBy')) {
      this.setAttribute('id', this.ariaLabelledBy ?? '');
    }
    if (_changedProperties.has('ariaControls')) {
      this.setAttribute('aria-controls', this.ariaControls ?? '');
    }
    if (_changedProperties.has('isActive')) {
      this.setAttribute('tabindex', this.isActive ? '0' : '-1');
      this.setAttribute('aria-selected', `${this.isActive}`);
    }

    if (_changedProperties.has('disabled')) {
      if (this.disabled) {
        this.setAttribute('aria-disabled', 'true');
      } else {
        this.removeAttribute('aria-disabled');
      }
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === KEYS.ENTER || event.key === KEYS.SPACE) {
      this.handleTabSelected();
    }
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-tab', {
      'cat-c-tab--large': this.size === 'lg' && this.variant !== 'contrast',
      'cat-c-tab--contrast': this.variant === 'contrast',
      'cat-c-tabs-span-container': this.variant === 'span-container',
      'cat-is-active': this.isActive,
      'cat-is-disabled': this.disabled,
      'cat-c-tab--responsive-full-width': this.behavior === 'responsive-full-width',
      'cat-c-tab--full-width': this.behavior === 'full-width'
    });
    return html`
      <div part="base" class="${componentClassName}">
        <div part="button" class="cat-c-tab__button">
          <div part="wrap" class="cat-c-tab__wrapper">
            <slot part="part-default"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-tab') === undefined) {
  customElements.define('cat-tab', BlocksTab);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-tab': BlocksTab;
  }
}
